import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, InputNumber, Popconfirm, Switch, message } from "antd";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "react-redux";
import {
  createNutritionTable,
  deleteNutritionTable,
  fetchNutritionTables,
  updateNutritionTable
} from "./nutrition-api";
import { field, lawAllowed, numberField, showError } from "./nutrition-utils";
import NutritionDrawer, { DrawerFooter } from "./nutrition-drawer";
import "./nutrition.css";

const TableCard = ({ table, onEdit, onDelete }) => {
  const id = numberField(table, "id");
  const max = numberField(table, "maxGuests");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`nutrition-table-card${isDragging ? " nutrition-table-card--dragging" : ""}`}
      onClick={() => onEdit(table)}
    >
      <div className="nutrition-table-card-head">
        <span className="nutrition-table-card-name">{field(table, "name", "")}</span>
      </div>

      <div className="nutrition-table-dots">
        {Array.from({ length: Math.min(max, 12) }).map((_, i) => <span key={i} className="nutrition-table-dot" />)}
        {max > 12 && <span className="nutrition-table-more">+{max - 12}</span>}
        {!max && <span className="nutrition-table-more">мест не указано</span>}
      </div>
      {numberField(table, "seatControl") === 1 && (
        <span className="nutrition-table-lock"><LockOutlined /> контроль мест</span>
      )}
      <div className="nutrition-table-card-actions">
        <Popconfirm title="Удалить стол?" onConfirm={() => onDelete(table)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      </div>
    </div>
  );
};

const NutritionHalls = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const law = useSelector((state) => state.user.session?.law);
  const allowed = lawAllowed(law, 84);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hallId, setHallId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const data = await fetchNutritionTables();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить залы");
    } finally {
      setLoading(false);
    }
  }, [allowed, messageApi]);
  useEffect(() => void load(), [load]);

  const halls = useMemo(
    () => rows.filter((r) => numberField(r, "idParent") === 0).sort((a, b) => numberField(a, "idx") - numberField(b, "idx")),
    [rows]
  );
  const currentHall = halls.find((h) => numberField(h, "id") === hallId) || halls[0] || null;
  const tables = useMemo(
    () => rows
      .filter((r) => currentHall && numberField(r, "idParent") === numberField(currentHall, "id"))
      .sort((a, b) => numberField(a, "idx") - numberField(b, "idx")),
    [rows, currentHall]
  );
  const hallStats = (hall) => {
    const ts = rows.filter((r) => numberField(r, "idParent") === numberField(hall, "id"));
    return `${ts.length} столов · ${ts.reduce((s, x) => s + numberField(x, "maxGuests"), 0)} мест`;
  };

  const openEditor = (row, parentId = 0) => {
    setEditor({ id: row ? numberField(row, "id") : null, parentId: row ? numberField(row, "idParent") : parentId });
    form.setFieldsValue({
      name: row ? field(row, "name", "") : "",
      idx: row ? numberField(row, "idx") : 0,
      maxGuests: row ? numberField(row, "maxGuests") : 0,
      seatControl: row ? numberField(row, "seatControl") === 1 : false
    });
  };

  const save = async () => {
    const values = await form.validateFields();
    const body = {
      idParent: editor.parentId,
      name: values.name.trim(),
      idx: values.idx || 0,
      maxGuests: values.maxGuests || 0,
      seatControl: Boolean(values.seatControl)
    };
    setSaving(true);
    try {
      if (editor.id) await updateNutritionTable(editor.id, body);
      else await createNutritionTable(body);
      messageApi.success("Сохранено");
      setEditor(null);
      await load();
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      await deleteNutritionTable(numberField(row, "id"));
      await load();
    } catch (error) {
      showError(messageApi, error, "Не удалось удалить");
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = tables.map((t) => numberField(t, "id"));
    const next = arrayMove(tables, ids.indexOf(active.id), ids.indexOf(over.id));
    const changed = next
      .map((t, i) => ({ table: t, idx: i + 1 }))
      .filter(({ table, idx }) => numberField(table, "idx") !== idx);
    setRows((prev) => prev.map((r) => {
      const c = changed.find(({ table }) => numberField(table, "id") === numberField(r, "id"));
      return c ? { ...r, idx: c.idx, Idx: c.idx } : r;
    }));
    try {
      for (const { table, idx } of changed) {
        await updateNutritionTable(numberField(table, "id"), {
          idParent: numberField(table, "idParent"),
          name: field(table, "name", ""),
          idx,
          maxGuests: numberField(table, "maxGuests"),
          seatControl: numberField(table, "seatControl") === 1
        });
      }
    } catch (error) {
      showError(messageApi, error, "Не удалось сохранить порядок столов");
      await load();
    }
  };

  if (!allowed) {
    return <div className="nutrition-page"><Alert type="error" showIcon message="Нет доступа к залам и столам (LAW84)" /></div>;
  }

  return (
    <div className="nutrition-page">
      {contextHolder}
      <div className="nutrition-toolbar">
        <h1 className="nutrition-title">Залы и столы</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null, 0)}>Добавить зал</Button>
      </div>
      <div className="nutrition-halls-body">
        <div className="nutrition-halls-list">
          {halls.map((hall) => (
            <div
              key={numberField(hall, "id")}
              className={`nutrition-hall-card${numberField(hall, "id") === numberField(currentHall, "id") ? " nutrition-hall-card--active" : ""}`}
              onClick={() => setHallId(numberField(hall, "id"))}
            >
              <div>
                <div className="nutrition-hall-name">{field(hall, "name", "")}</div>
                <div className="nutrition-hall-meta">{hallStats(hall)}</div>
              </div>
              <div className="nutrition-hall-actions" onClick={(e) => e.stopPropagation()}>
                <Popconfirm title="Удалить зал?" onConfirm={() => remove(hall)}>
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          ))}
          {!halls.length && !loading && <div className="nutrition-empty">Залы не созданы</div>}
        </div>
        <div className="nutrition-board">
          {currentHall ? (
            <>
              <div className="nutrition-board-head" title="Редактировать зал" onClick={() => openEditor(currentHall)}>
                <div className="nutrition-board-head-name">{field(currentHall, "name", "")}</div>
                <div className="nutrition-board-head-meta">{hallStats(currentHall)}</div>
              </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={tables.map((t) => numberField(t, "id"))} strategy={rectSortingStrategy}>
                <div className="nutrition-board-grid">
                  {tables.map((table) => (
                    <TableCard key={numberField(table, "id")} table={table} onEdit={openEditor} onDelete={remove} />
                  ))}
                  <div className="nutrition-table-add" onClick={() => openEditor(null, numberField(currentHall, "id"))}>
                    <PlusOutlined /> Добавить стол
                  </div>
                </div>
              </SortableContext>
            </DndContext>
            </>
          ) : null}
          {!currentHall && !loading && <div className="nutrition-empty">Выберите или создайте зал</div>}
        </div>
      </div>
      <NutritionDrawer
        open={Boolean(editor)}
        title={editor?.parentId ? "Стол" : "Зал"}
        onClose={() => setEditor(null)}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, whitespace: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="idx" label="Порядок"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
          {editor?.parentId ? (
            <>
              <Form.Item name="maxGuests" label="Максимум гостей"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
              <Form.Item name="seatControl" label="Контроль количества мест" valuePropName="checked"><Switch /></Form.Item>
            </>
          ) : null}
          <DrawerFooter onSave={save} onClose={() => setEditor(null)} saving={saving} />
        </Form>
      </NutritionDrawer>
    </div>
  );
};

export default NutritionHalls;
